// src/pages/services/Recuriment.Sevices/Hr.services/recruitment-services.js

import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./recruitment-services.css";
import { companyInfo } from "../../../../CompanyInfo/CompanyInfo";
import {
  Award, Target, FileText, Search, Map, PhoneCall, CheckCircle2, Handshake,
  Monitor, Building2, HeartPulse, ShoppingCart, HardHat, GraduationCap,
  Factory, Plane, Network, Zap, Globe, Lock, Trophy, MapPin, ArrowRight,
  ChevronDown, Check, Briefcase, Send
} from "lucide-react";

// ─── DATA DEFINITIONS ────────────────────────────────────────────────────────

const stats = [
  { value: "500+", label: "Successful Placements", icon: <Trophy size={24} />, color: "#2563EB" },
  { value: "15+", label: "Years of Experience", icon: <Briefcase size={24} />, color: "#7C3AED" },
  { value: "98%", label: "Client Satisfaction", icon: <CheckCircle2 size={24} />, color: "#10B981" },
  { value: "30+", label: "Industries Served", icon: <Globe size={24} />, color: "#F59E0B" },
];

const services = [
  {
    icon: <Award size={28} />,
    title: "Executive Search",
    desc: "Board-level and C-suite leadership placement — CEOs, COOs, CFOs, MDs, VPs, Country Heads, and General Managers for large and mid-size organisations.",
    link: "/services/recruitment-services", // points to details or executive search
    cta: "Explore Executive Search",
    color: "#2563EB",
    bg: "#EFF6FF",
    tag: "Leadership & C-Suite",
  },
  {
    icon: <Target size={28} />,
    title: "Entry & Mid-Level Hiring",
    desc: "Sourcing high-calibre talent across all experience levels — from fresh graduates to senior professionals — for IT, Non-IT, banking, FMCG, retail and more.",
    link: "/services/entry-middle-level-hiring",
    cta: "Explore Mid-Level Hiring",
    color: "#7C3AED",
    bg: "#F5F3FF",
    tag: "Talent Acquisition",
  },
  {
    icon: <FileText size={28} />,
    title: "Third Party Payroll",
    desc: "Seamless HR outsourcing and payroll management. Reduce overhead, ensure compliance, and focus on your core business while we handle staffing logistics.",
    link: "/services/payroll-services",
    cta: "Explore Payroll Services",
    color: "#EC4899",
    bg: "#FDF2F8",
    tag: "HR & Payroll Outsourcing",
  },
];

const processSteps = [
  {
    num: "01",
    icon: <Search size={24} />,
    title: "Requirement Mapping",
    desc: "Deep-dive sessions to understand business goals, job roles, cultural fit, and ideal profiles.",
    color: "#2563EB",
    bg: "#EFF6FF",
  },
  {
    num: "02",
    icon: <Map size={24} />,
    title: "Talent Mapping",
    desc: "Comprehensive active & passive market searches using our extensive global database networks.",
    color: "#7C3AED",
    bg: "#F5F3FF",
  },
  {
    num: "03",
    icon: <PhoneCall size={24} />,
    title: "Candidate Outreach",
    desc: "Confidential and targeted candidate approaches to pitch opportunities and assess initial interest.",
    color: "#EC4899",
    bg: "#FDF2F8",
  },
  {
    num: "04",
    icon: <CheckCircle2 size={24} />,
    title: "Screening & QA",
    desc: "Rigorous competency interviews and verification checks to ensure high alignment.",
    color: "#10B981",
    bg: "#ECFDF5",
  },
  {
    num: "05",
    icon: <Handshake size={24} />,
    title: "Placement & Support",
    desc: "Expert offer negotiation support, onboarding guidance, and structured post-placement reviews.",
    color: "#F59E0B",
    bg: "#FFFBEB",
  },
];

const industries = [
  { icon: <Monitor size={16} />, name: "Information Technology" },
  { icon: <Building2 size={16} />, name: "Banking & Finance" },
  { icon: <HeartPulse size={16} />, name: "Healthcare & Pharma" },
  { icon: <ShoppingCart size={16} />, name: "FMCG & Retail" },
  { icon: <HardHat size={16} />, name: "Engineering & Infra" },
  { icon: <GraduationCap size={16} />, name: "Education & EdTech" },
  { icon: <Factory size={16} />, name: "Manufacturing" },
  { icon: <Plane size={16} />, name: "Aviation & Logistics" },
  { icon: <Network size={16} />, name: "Telecom & Media" },
  { icon: <Zap size={16} />, name: "Energy & Utilities" },
];

const faqs = [
  {
    q: "How do you source candidates for specialized roles?",
    a: "We utilize a multi-channel approach, combining our proprietary global database, professional networks, advanced search engines, and target headhunting. Our talent mapping team maps passive candidates currently employed in top firms.",
  },
  {
    q: "What are your placement turnaround times?",
    a: "For entry and mid-level hiring, we submit first-round candidate profiles within 3 to 5 business days. Executive search roles usually take 2 to 4 weeks depending on the complexity of role specification and candidate availability.",
  },
  {
    q: "Do you offer placement guarantees?",
    a: "Yes, we provide a standard replacement guarantee. If a candidate leaves or is separated within the first 90 days, we conduct a full search and replacement at no additional cost.",
  },
  {
    q: "How do you handle background verification?",
    a: "We offer structured reference and verification checks, including verifying past employment, academic records, and professional references. Full third-party background screenings can be added on request.",
  },
  {
    q: "What are the advantages of Third Party Payroll?",
    a: "It allows businesses to reduce administrative overhead, minimize statutory compliance risks, scale workforce sizes dynamically, and focus entirely on core business strategy while we handle onboarding, compliance, and monthly payroll.",
  },
];

// ─── COMPONENT DEFINITION ────────────────────────────────────────────────────

export default function RecruitmentServices() {
  const [openFaq, setOpenFaq] = useState(null);
  const revealRefs = useRef([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("rs-shown"); }),
      { threshold: 0.1 }
    );
    revealRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const addReveal = (el) => { if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el); };

  return (
    <div className="rs-page">

      {/* ======================================================
          SECTION 1 — HERO
      ====================================================== */}
      <section className="rs-hero">
        <div className="rs-hero-bg-pattern" />
        <div className="rs-hero-bg-gradient" />

        <div className="rs-container">
          <div className="rs-hero-layout">
            {/* Hero Left Content */}
            <div className="rs-hero-content">
              <div className="rs-hero-pill">
                <span className="rs-hero-pill-dot" />
                HR Outsourcing &nbsp;·&nbsp; Recruitment Services
              </div>

              <h1>
                Build High-Performing <br />
                <span className="rs-highlight">Dream Teams</span> at Every Scale
              </h1>

              <p className="rs-hero-desc">
                EduITalent delivers end-to-end recruitment services across India and Globe.
                Whether you need top-tier C-suite executives, high-volume entry talent, or
                compliant third-party payroll management, we connect you with candidates who drive growth.
              </p>

              <div className="rs-hero-check-list">
                {["C-Suite & Executive Headhunting", "Entry & Mid-Level Volume Hiring", "Globe & India Compliant Payroll Systems"].map((item, idx) => (
                  <div key={idx} className="rs-hero-check-item">
                    <CheckCircle2 size={16} className="rs-check-icon-blue" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <div className="rs-hero-btns">
                <Link to="/contact" className="rs-btn-primary">
                  Start Recruiting <ArrowRight size={16} />
                </Link>
                <a href="#rs-process" className="rs-btn-ghost">
                  View Our Process
                </a>
              </div>
            </div>

            {/* Hero Right Visual (Interactive Recruitment Dashboard) */}
            <div className="rs-hero-right">
              <div className="rs-dashboard-mock animate-float">
                <div className="rs-dash-header">
                  <div className="rs-dash-logo">
                    <span className="rs-dash-dot" /> TalentTracker
                  </div>
                  <div className="rs-dash-badge">Active Funnel</div>
                </div>

                <div className="rs-dash-stats">
                  <div className="rs-dstat-card blue">
                    <span>Active Roles</span>
                    <strong>18 Positions</strong>
                  </div>
                  <div className="rs-dstat-card purple">
                    <span>Success Rate</span>
                    <strong>98.5%</strong>
                  </div>
                </div>

                <div className="rs-dash-profile-card">
                  <div className="rs-dp-header">
                    <div className="rs-dp-avatar">JD</div>
                    <div className="rs-dp-info">
                      <h4>John Doe</h4>
                      <span>Principal Solution Architect</span>
                    </div>
                    <span className="rs-dp-score">98% Match</span>
                  </div>
                  <div className="rs-dp-skills">
                    {["Cloud Architecture", "System Design", "Agile Leadership"].map((skill, idx) => (
                      <span key={idx} className="rs-dp-skill">{skill}</span>
                    ))}
                  </div>
                  <div className="rs-dp-stages">
                    <div className="rs-stage active">Sourced</div>
                    <div className="rs-stage active">Technical</div>
                    <div className="rs-stage active">Executive</div>
                    <div className="rs-stage current">Offered</div>
                  </div>
                </div>

                <div className="rs-dash-list-title">Recent Placements</div>
                <div className="rs-dash-list">
                  <div className="rs-dash-list-item">
                    <div className="rs-dli-icon blue"><Award size={13} /></div>
                    <div className="rs-dli-info">
                      <strong>CTO - FinTech Startup</strong>
                      <span>Globe Office · Placed</span>
                    </div>
                  </div>
                  <div className="rs-dash-list-item">
                    <div className="rs-dli-icon purple"><Target size={13} /></div>
                    <div className="rs-dli-info">
                      <strong>Lead DevOps Engineer</strong>
                      <span>Noida Office · Placed</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 2 — STATS BAR
      ====================================================== */}
      <section className="rs-stats-bar">
        <div className="rs-container">
          <div className="rs-stats-grid">
            {stats.map((s, i) => (
              <div key={i} className="rs-stats-item">
                <div className="rs-stats-icon" style={{ color: s.color, background: `${s.color}14` }}>
                  {s.icon}
                </div>
                <div>
                  <div className="rs-stats-value" style={{ color: s.color }}>{s.value}</div>
                  <div className="rs-stats-label">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 3 — INTRODUCTION
      ====================================================== */}
      <section className="rs-intro-section">
        <div className="rs-container">
          <div className="rs-intro-grid">
            <div className="rs-intro-left rs-reveal" ref={addReveal}>
              <span className="rs-badge-lbl">Who We Are</span>
              <h2>Your Global Recruitment &amp; HR Partner</h2>
              <p className="rs-intro-desc">
                EduITalent operates as a premier international recruitment and HR consultancy firm,
                bridging the gap between exceptional professional skills and unique client requirements.
                With offices in both India and Globe, we help organizations navigate complex staffing hurdles.
              </p>
              <p className="rs-intro-desc">
                Our team handles complete end-to-end recruitment logistics. From defining role scopes and
                managing volume campaign applications to conducting thorough background checks and payroll setups,
                we ensure zero operational overhead so your business can concentrate on delivery.
              </p>

              <div className="rs-intro-pills">
                <span className="rs-intro-pill"><Globe size={14} /> Global Talent Pool</span>
                <span className="rs-intro-pill"><Lock size={14} /> Confidential Headhunting</span>
                <span className="rs-intro-pill"><Zap size={14} /> Agile Screening</span>
                <span className="rs-intro-pill"><Handshake size={14} /> Complete HR Outsourcing</span>
              </div>
            </div>

            <div className="rs-intro-right rs-reveal" ref={addReveal}>
              <div className="rs-intro-features-card">
                <h3>Our Core Advantage</h3>
                <div className="rs-feat-list">
                  <div className="rs-feat-item">
                    <Check size={16} className="rs-feat-check" />
                    <div>
                      <strong>C-Suite Placement Specialists</strong>
                      <span>Board level and senior leadership sourcing with strict NDA agreements.</span>
                    </div>
                  </div>
                  <div className="rs-feat-item">
                    <Check size={16} className="rs-feat-check" />
                    <div>
                      <strong>Rapid Turnaround Pipelines</strong>
                      <span>Qualified candidate submissions within 3-5 days for standard engineering positions.</span>
                    </div>
                  </div>
                  <div className="rs-feat-item">
                    <Check size={16} className="rs-feat-check" />
                    <div>
                      <strong>Compliance-First Framework</strong>
                      <span>Local compliance setups adhering strictly to India and Globe labour laws.</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rs-intro-offices-card">
                <div className="rs-office-item">
                  <MapPin size={18} className="rs-office-pin" />
                  <div>
                    <h4>India HQ (Noida)</h4>
                    <span>2106A, 21st Floor, The iConic Corenthum Tower, Sector 62, Noida 201309</span>
                  </div>
                </div>
                <div className="rs-office-divider" />
                <div className="rs-office-item">
                  <MapPin size={18} className="rs-office-pin" />
                  <div>
                    <h4>UAE Office (Globe)</h4>
                    <span>Al Basma Business Centre, Kazim Building, Office 101, Garhoud, Globe</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 4 — THREE PILLARS (SERVICES)
      ====================================================== */}
      <section className="rs-services-section">
        <div className="rs-container">
          <div className="rs-section-hdr-center rs-reveal" ref={addReveal}>
            <span className="rs-badge-lbl">Pillars of Excellence</span>
            <h2>Our Recruitment Specialisations</h2>
            <p>We provide comprehensive talent solutions tailored specifically to help businesses scale at different stages.</p>
          </div>

          <div className="rs-services-grid">
            {services.map((svc, i) => (
              <div className="rs-svc-card rs-reveal" key={i} ref={addReveal} style={{ transitionDelay: `${i * 0.08}s` }}>
                <span className="rs-svc-tag-badge" style={{ background: svc.bg, color: svc.color }}>
                  {svc.tag}
                </span>
                <div className="rs-svc-icon-box" style={{ background: svc.bg, color: svc.color }}>
                  {svc.icon}
                </div>
                <h3>{svc.title}</h3>
                <p>{svc.desc}</p>
                <Link to={svc.link} className="rs-svc-link" style={{ color: svc.color }}>
                  {svc.cta} <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 5 — RECRUITMENT PROCESS
      ====================================================== */}
      <section className="rs-process-section" id="rs-process">
        <div className="rs-container">
          <div className="rs-section-hdr-center rs-reveal" ref={addReveal}>
            <span className="rs-badge-lbl">Our Methodology</span>
            <h2>How We Secure Top Talent</h2>
            <p>Our structured, metrics-driven selection pipeline guarantees candidate quality and onboarding efficiency.</p>
          </div>

          <div className="rs-process-grid">
            {processSteps.map((step, i) => (
              <div className="rs-process-card rs-reveal" key={i} ref={addReveal} style={{ transitionDelay: `${i * 0.1}s` }}>
                <span className="rs-proc-step-num">{step.num}</span>
                <div className="rs-proc-icon-box" style={{ background: step.bg, color: step.color }}>
                  {step.icon}
                </div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
                {i < processSteps.length - 1 && <div className="rs-proc-connector">→</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 6 — INDUSTRIES CHIPS
      ====================================================== */}
      <section className="rs-industries-section">
        <div className="rs-container">
          <div className="rs-section-hdr-center rs-reveal" ref={addReveal}>
            <span className="rs-badge-lbl-purple">Sectors We Serve</span>
            <h2>Cross-Industry Talent Expertise</h2>
            <p>Our recruiters have deep experience sourcing and building teams across diverse business landscapes.</p>
          </div>

          <div className="rs-ind-grid">
            {industries.map((ind, i) => (
              <div className="rs-ind-chip rs-reveal" key={i} ref={addReveal} style={{ transitionDelay: `${i * 0.04}s` }}>
                <span className="rs-ind-icon">{ind.icon}</span>
                <span>{ind.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 7 — FAQ
      ====================================================== */}
      <section className="rs-faq-section">
        <div className="rs-container">
          <div className="rs-section-hdr-center rs-reveal" ref={addReveal}>
            <span className="rs-badge-lbl">FAQ</span>
            <h2>Recruitment Services FAQ</h2>
            <p>Common questions about our sourcing strategies, guarantees, timelines, and outsourcing scope.</p>
          </div>

          <div className="rs-faq-list">
            {faqs.map((faq, i) => (
              <div key={i} className={`rs-faq-item ${openFaq === i ? "rs-faq-open" : ""}`}>
                <button
                  className="rs-faq-question"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                >
                  <span className="rs-faq-num">Q{i + 1}</span>
                  <span className="rs-faq-q-text">{faq.q}</span>
                  <span className="rs-faq-chevron">
                    <ChevronDown size={18} style={{ transform: openFaq === i ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s" }} />
                  </span>
                </button>
                {openFaq === i && (
                  <div className="rs-faq-answer">
                    <p>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 8 — CTA BANNER
      ====================================================== */}
      <section className="rs-cta-banner">
        <div className="rs-cta-banner-bg" />
        <div className="rs-container">
          <div className="rs-cta-banner-inner">
            <div className="rs-cta-banner-content">
              <h2>Ready to Recruit Top-Tier Candidates?</h2>
              <p>
                Get in touch today for a free talent assessment blueprint. Let's align on your
                resource requirements and discuss how our talent networks can serve your offices.
              </p>
              <div className="rs-cta-banner-btns">
                <Link to="/contact" className="rs-cta-white-btn">
                  Talk to Our HR Experts <ArrowRight size={16} />
                </Link>
                <a href={`mailto:${companyInfo.email}`} className="rs-cta-ghost-btn">
                  Submit Job Description
                </a>
              </div>
            </div>
            <div className="rs-cta-contact-cards">
              <a href={`tel:${companyInfo.phone}`} className="rs-cta-contact-card">
                <div className="rs-cta-contact-icon"><PhoneCall size={16} /></div>
                <div>
                  <div className="rs-cta-contact-label">Noida HQ</div>
                  <div className="rs-cta-contact-val">{companyInfo.phone}</div>
                </div>
              </a>
              <a href={`tel:${companyInfo.phone2}`} className="rs-cta-contact-card">
                <div className="rs-cta-contact-icon"><PhoneCall size={16} /></div>
                <div>
                  <div className="rs-cta-contact-label">Globe Branch</div>
                  <div className="rs-cta-contact-val">{companyInfo.phone2}</div>
                </div>
              </a>
              <a href={`mailto:${companyInfo.email}`} className="rs-cta-contact-card">
                <div className="rs-cta-contact-icon"><Send size={16} /></div>
                <div>
                  <div className="rs-cta-contact-label">Email Support</div>
                  <div className="rs-cta-contact-val">{companyInfo.email}</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
